import axios from "axios";
import React, { forwardRef, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Swal from "sweetalert2";
import "./css/main.login.css";
import "./css/login-page.css";
import { getAssetUrl } from "../../utils/assetUrl";
import HackerLiveFeed from "../../components/HackerLiveFeed";

const registerSchema = yup.object().shape({
  username: yup
    .string()
    .transform((value) => (value ? value.toLowerCase().trim() : value))
    .required("Tên tài khoản bắt buộc"),
  phone_number: yup
    .string()
    .matches(/^\d{10}$/, "Số điện thoại phải có 10 chữ số")
    .required("Số điện thoại bắt buộc"),
  password: yup.string().min(6, "Mật khẩu ít nhất 6 ký tự").required("Mật khẩu bắt buộc"),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref("password")], "Mật khẩu xác nhận không khớp")
    .required("Vui lòng xác nhận mật khẩu"),
});

type RegisterFormValues = yup.InferType<typeof registerSchema>;

type AuthFieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  icon: string;
  showToggle?: boolean;
  visible?: boolean;
  onToggle?: () => void;
};

const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(function AuthField(
  { icon, placeholder, type = "text", showToggle, visible, onToggle, className, ...inputProps },
  ref
) {
  return (
    <div className={`login-field${className ? ` ${className}` : ""}`}>
      <img className="login-field__icon" src={getAssetUrl(icon)} alt="" />
      <input
        ref={ref}
        className="login-field__input"
        type={showToggle ? (visible ? "text" : "password") : type}
        placeholder={placeholder}
        {...inputProps}
      />
      {showToggle ? (
        <button
          type="button"
          className="login-field__eye"
          onClick={onToggle}
          aria-label={visible ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
        >
          <img src={getAssetUrl("/assets/llwin/icon-eye.svg")} alt="" />
        </button>
      ) : null}
    </div>
  );
});

const Login: React.FC = () => {
  const Cookies = require("js-cookie");

  const [authView, setAuthView] = useState<"login" | "register">("login");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegPassword2, setShowRegPassword2] = useState(false);
  const [referralCode, setReferralCode] = useState("");

  const {
    register: registerField,
    handleSubmit: handleRegisterSubmit,
    reset: resetRegisterForm,
    formState: { errors: registerErrors },
  } = useForm<RegisterFormValues>({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      username: "",
      phone_number: "",
      password: "",
      password_confirmation: "",
    },
  });

  useEffect(() => {
    document.documentElement.classList.add("login-scroll-lock");
    document.body.classList.add("login-scroll-lock");
    return () => {
      document.documentElement.classList.remove("login-scroll-lock");
      document.body.classList.remove("login-scroll-lock");
    };
  }, []);

  const handleLogin = async () => {
    const cleanUsername = userName.trim().toLowerCase();
    if (cleanUsername === "" || password.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Lỗi đăng nhập",
        text: "Vui lòng điền đầy đủ tài khoản và mật khẩu!",
        customClass: {
          popup: "custom-swal",
          title: "custom-title",
          htmlContainer: "custom-text",
        },
      });
      return;
    }

    try {
      Swal.fire({
        title: "Đang kết nối Server GOBING...",
        html: "<p class='swal-loading-subtext'>Hệ thống đang xác thực danh tính...</p><div class='swal-loading-dots'><span class='swal-dot swal-dot-cyan'></span><span class='swal-dot swal-dot-orange'></span></div>",
        customClass: {
          popup: "custom-swal swal-loading-modal",
          title: "custom-title",
          htmlContainer: "custom-text",
        },
        allowOutsideClick: false,
        showConfirmButton: false,
      });

      await new Promise((resolve) => setTimeout(resolve, 800));
      await axios
        .post(`${process.env.REACT_APP_URL_API}/auth/login`, {
          username: cleanUsername,
          password,
        })
        .then(async (data) => {
          localStorage.setItem(
            "user_info",
            JSON.stringify({
              userName: data.data.user.username,
              coins: data.data.user.coins,
              role: data.data.user.role,
              id: data.data.user._id,
            })
          );

          Cookies.set("access_token", data.data.access_token, {
            expires: 1 / 24,
            secure: false,
            sameSite: "Lax",
          });

          await new Promise((resolve) => setTimeout(resolve, 100));

          if (
            (data.status === 200 || data.status === 201) &&
            (data.data.user.role === "SUPERADMIN" || data.data.user.role === "ADMIN")
          ) {
            Swal.fire({
              icon: "success",
              title: "Đăng nhập thành công",
              text: "Đang chuyển hướng trang Quản trị...",
              customClass: {
                popup: "custom-swal",
                title: "custom-title",
                htmlContainer: "custom-text",
              },
            });
            setTimeout(() => {
              window.location.href = "/admin";
            }, 500);
          } else {
            Swal.fire({
              icon: "success",
              title: "Đăng nhập thành công",
              text: "Chào mừng bạn đến với GOBING VIP!",
              customClass: {
                popup: "custom-swal",
                title: "custom-title",
                htmlContainer: "custom-text",
              },
            });
            setTimeout(() => {
              window.location.href = "/";
            }, 500);
          }
        })
        .catch((err) => {
          if (err.response?.status === 401) {
            Swal.fire({
              icon: "error",
              title: "Lỗi đăng nhập",
              text: "Tài khoản hoặc mật khẩu không chính xác!",
              customClass: {
                popup: "custom-swal",
                title: "custom-title",
                htmlContainer: "custom-text",
              },
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Lỗi máy chủ",
              text: "Không thể kết nối đến máy chủ GOBING, vui lòng thử lại!",
              customClass: {
                popup: "custom-swal",
                title: "custom-title",
                htmlContainer: "custom-text",
              },
            });
          }
        });
    } catch (error) {
      return error;
    }
  };

  const onRegister = async (data: RegisterFormValues) => {
    try {
      const cleanUsername = (data.username || "").trim().toLowerCase();
      Swal.fire({
        title: "Đang khởi tạo tài khoản...",
        html: "<p class='swal-loading-subtext'>Đang đăng ký thành viên mới...</p>",
        customClass: {
          popup: "custom-swal swal-loading-modal",
          title: "custom-title",
          htmlContainer: "custom-text",
        },
        allowOutsideClick: false,
        showConfirmButton: false,
      });

      await axios
        .post(`${process.env.REACT_APP_URL_API}/auth/register`, {
          username: cleanUsername,
          phone: data.phone_number,
          password: String(data.password),
          ...(referralCode.trim() ? { referral_code: referralCode.trim() } : {}),
        })
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            Swal.fire({
              icon: "success",
              title: "Đăng ký thành công!",
              text: "Vui lòng đăng nhập bằng tài khoản vừa tạo.",
              customClass: {
                popup: "custom-swal",
                title: "custom-title",
                htmlContainer: "custom-text",
              },
            });
            resetRegisterForm();
            setAuthView("login");
          }
        })
        .catch((err) => {
          Swal.fire({
            icon: "error",
            title: "Lỗi đăng ký",
            text: `${err.response?.data?.message || "Có lỗi xảy ra, vui lòng thử lại!"}`,
            customClass: {
              popup: "custom-swal",
              title: "custom-title",
              htmlContainer: "custom-text",
            },
          });
        });
    } catch (error) {
      return error;
    }
  };

  const goRegister = () => {
    setAuthView("register");
    setReferralCode("");
    resetRegisterForm();
  };

  const goLogin = () => {
    setAuthView("login");
    resetRegisterForm();
  };

  return (
    <div className="login-page">
      <div className="login-page__brand">
        <div className="login-page__logo-clip">
          <img src={getAssetUrl("/assets/llwin/logo.png")} alt="Super setting tool" />
        </div>
        <p className="login-page__tagline">
          &gt;Tool tích hợp công nghệ ai agent phân tích siêu chuẩn&lt;
        </p>
      </div>

      <div
        className="login-page__panel"
        role="dialog"
        aria-label={authView === "login" ? "Đăng nhập" : "Đăng ký"}
      >
        <img
          className="login-page__edge login-page__edge--left"
          src={getAssetUrl("/assets/llwin/frame-corner-left.svg")}
          alt=""
        />
        <img
          className="login-page__edge login-page__edge--right"
          src={getAssetUrl("/assets/llwin/frame-corner-right.svg")}
          alt=""
        />
        <img
          className="login-page__robot"
          src={getAssetUrl("/assets/llwin/robot.png")}
          alt=""
        />

        <div className="login-page__form-side">
          <h1 className="login-page__form-title">
            {authView === "login" ? "đăng nhập" : "đăng ký"}
          </h1>

          {authView === "login" ? (
            <form
              className="login-page__fields"
              onSubmit={(e) => {
                e.preventDefault();
                handleLogin();
              }}
            >
              <AuthField
                icon="/assets/llwin/icon-user.png"
                placeholder="Nhập tên đăng nhập"
                value={userName}
                onChange={(e) => setUserName(e.target.value.toLowerCase())}
                style={{ textTransform: "lowercase" }}
                autoComplete="username"
                maxLength={20}
              />
              <AuthField
                icon="/assets/llwin/icon-lock.png"
                placeholder="Nhập mật khẩu"
                showToggle
                visible={showPassword}
                onToggle={() => setShowPassword((v) => !v)}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                maxLength={24}
              />
              <button type="submit" className="login-page__submit-btn">
                &gt; ĐĂNG NHẬP &lt;
              </button>
              <p className="login-page__footer-nav">
                chưa có tài khoản?{" "}
                <button type="button" className="login-page__switch-btn" onClick={goRegister}>
                  Link Đăng Ký
                </button>
              </p>
            </form>
          ) : (
            <form
              className="login-page__fields"
              onSubmit={handleRegisterSubmit(onRegister)}
              noValidate
            >
              <div>
                <AuthField
                  icon="/assets/llwin/icon-user.png"
                  placeholder="Nhập tên đăng nhập"
                  autoComplete="username"
                  style={{ textTransform: "lowercase" }}
                  {...registerField("username", {
                    onChange: (e: any) => {
                      e.target.value = (e.target.value || "").toLowerCase();
                    },
                  })}
                />
                {registerErrors.username && (
                  <p className="login-page__field-error">{registerErrors.username.message}</p>
                )}
              </div>
              <div>
                <AuthField
                  icon="/assets/llwin/icon-lock.png"
                  placeholder="Nhập mật khẩu"
                  showToggle
                  visible={showRegPassword}
                  onToggle={() => setShowRegPassword((v) => !v)}
                  autoComplete="new-password"
                  {...registerField("password")}
                />
                {registerErrors.password && (
                  <p className="login-page__field-error">{registerErrors.password.message}</p>
                )}
              </div>
              <div>
                <AuthField
                  icon="/assets/llwin/icon-lock.png"
                  placeholder="Nhập lại mật khẩu"
                  showToggle
                  visible={showRegPassword2}
                  onToggle={() => setShowRegPassword2((v) => !v)}
                  autoComplete="new-password"
                  {...registerField("password_confirmation")}
                />
                {registerErrors.password_confirmation && (
                  <p className="login-page__field-error">
                    {registerErrors.password_confirmation.message}
                  </p>
                )}
              </div>
              <div>
                <AuthField
                  icon="/assets/llwin/icon-phone.png"
                  placeholder="+84..."
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel"
                  maxLength={10}
                  {...registerField("phone_number")}
                />
                {registerErrors.phone_number && (
                  <p className="login-page__field-error">{registerErrors.phone_number.message}</p>
                )}
              </div>
              <AuthField
                className="login-field--ref"
                icon="/assets/llwin/icon-ref.png"
                placeholder="Nhập mã giới thiệu (nếu có)"
                autoComplete="off"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
              />
              <button type="submit" className="login-page__submit-btn">
                &gt; ĐĂNG KÝ &lt;
              </button>
              <p className="login-page__footer-nav">
                Đã có tài khoản?{" "}
                <button type="button" className="login-page__switch-btn" onClick={goLogin}>
                  Link Đăng nhập
                </button>
              </p>
            </form>
          )}
        </div>
      </div>

      <HackerLiveFeed side="left" variant="terminal" />
      <HackerLiveFeed side="right" variant="live" />
    </div>
  );
};

export default Login;
